import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Bell, BellOff, Send, ChevronRight, Smartphone, Info } from "lucide-react";
import { ScreenTitle } from "@/components/ScreenTitle";
import {
  isPushSupported, isStandalone, isIOSSafari,
  subscribePush, unsubscribePush, getCurrentEndpoint, sendTestPush,
} from "@/lib/push";

export default function PoliticsSettingsPage() {
  const [cats, setCats] = useState([]);
  const [subEndpoint, setSubEndpoint] = useState(null);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const supported = isPushSupported();
  const standalone = isStandalone();
  const iOS = isIOSSafari();

  const loadCats = async () => {
    try { const r = await api.get("/politics/categories"); setCats(r.data); } catch (e) {}
  };

  const loadSub = async () => {
    if (!supported) return;
    const ep = await getCurrentEndpoint();
    setSubEndpoint(ep);
    if (ep) {
      try {
        const r = await api.get("/push/subscription", { params: { endpoint: ep } });
        setSub(r.data && r.data.endpoint ? r.data : null);
      } catch (e) { setSub(null); }
    } else { setSub(null); }
  };

  useEffect(() => { loadCats(); loadSub(); }, []); // eslint-disable-line

  const activate = async () => {
    if (iOS && !standalone) {
      toast.error("Sur iPhone, installe d'abord AIMKA sur l'écran d'accueil (Safari → Partager → Sur l'écran d'accueil).");
      return;
    }
    setBusy(true);
    try {
      await subscribePush();
      await loadSub();
      toast.success("Notifications activées sur cet appareil");
    } catch (e) {
      toast.error(e.message || "Activation impossible");
    } finally { setBusy(false); }
  };

  const deactivate = async () => {
    setBusy(true);
    try {
      await unsubscribePush();
      await loadSub();
      toast.success("Notifications désactivées sur cet appareil");
    } catch (e) {
      toast.error("Désactivation impossible");
    } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try {
      const ok = await sendTestPush();
      toast[ok ? "success" : "error"](ok ? "Push de test envoyé" : "Impossible d'envoyer le test");
    } catch (e) { toast.error(e.message || "Test impossible"); }
    finally { setBusy(false); }
  };

  const toggleCategoryOptIn = async (catId) => {
    if (!subEndpoint) return;
    const current = sub?.category_ids;
    // null (or absent) means "all categories" — turning off one splits into an explicit array
    let next;
    if (current === null || current === undefined) {
      next = cats.filter((c) => c.id !== catId).map((c) => c.id);
    } else if (current.includes(catId)) {
      next = current.filter((x) => x !== catId);
    } else {
      next = [...current, catId];
    }
    // If user re-enables all, revert to null (implicit "all")
    if (next.length === cats.length) next = null;
    try {
      await api.patch("/push/subscription", { category_ids: next }, { params: { endpoint: subEndpoint } });
      await loadSub();
    } catch (e) { toast.error("Impossible"); }
  };

  const isOptedIn = (catId) => {
    if (!sub) return false;
    if (!sub.category_ids) return true; // null/absent = all
    return sub.category_ids.includes(catId);
  };

  return (
    <div data-testid="politics-settings-page">
      <ScreenTitle eyebrow="Recommandation d'actu" title="Réglages veille" />

      <div className="px-5 pb-5 space-y-6">

        {/* Categories link */}
        <section className="border border-[var(--border-default)] p-4 bg-white">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <h3 className="font-serif text-lg">Catégories</h3>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                Chaque catégorie a ses propres flux, mots-clés, fréquence, Telegram, canal urgent.
              </p>
            </div>
            <Link to="/veille/categories" data-testid="link-categories"
              className="min-h-[44px] px-3 flex items-center gap-1 border border-black bg-white hover:bg-black hover:text-white text-[11px] font-mono uppercase tracking-widest">
              Gérer <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </section>

        {/* Notifications */}
        <section className="border border-[var(--border-default)] p-4 space-y-3 bg-white" data-testid="notif-section">
          <h3 className="font-serif text-lg flex items-center gap-2">
            <Smartphone className="w-4 h-4" /> Notifications push
          </h3>

          {!supported && (
            <p className="text-sm text-[var(--accent-strong)]">
              Ce navigateur ne supporte pas les notifications push web.
            </p>
          )}

          {supported && iOS && !standalone && (
            <div className="border border-[var(--accent)] bg-[var(--bg-app)] p-3 text-xs leading-relaxed" data-testid="ios-hint">
              <p className="font-mono uppercase tracking-widest text-[11px] mb-1 flex items-center gap-1">
                <Info className="w-3.5 h-3.5" /> iPhone — installe l'app d'abord
              </p>
              <p>
                Pour recevoir des notifications sur iOS, il faut d'abord ajouter AIMKA à l'écran d'accueil :
              </p>
              <ol className="list-decimal pl-5 mt-1 space-y-0.5">
                <li>Appuie sur l'icône <strong>Partager</strong> en bas de Safari.</li>
                <li>Choisis <strong>« Sur l'écran d'accueil »</strong>.</li>
                <li>Rouvre AIMKA depuis l'icône ainsi créée puis reviens dans ces réglages.</li>
              </ol>
            </div>
          )}

          {supported && (
            <>
              <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                État · {sub ? "abonné" : "non abonné"} {standalone && " · installé"}
              </div>
              <div className="flex flex-wrap gap-2">
                {!sub ? (
                  <button onClick={activate} disabled={busy || (iOS && !standalone)} data-testid="push-activate"
                    className="min-h-[44px] px-4 flex items-center gap-2 bg-black text-white text-[11px] uppercase tracking-widest font-mono hover:bg-[#222] disabled:opacity-60">
                    <Bell className="w-4 h-4" /> Activer sur cet appareil
                  </button>
                ) : (
                  <>
                    <button onClick={test} disabled={busy} data-testid="push-test"
                      className="min-h-[44px] px-4 flex items-center gap-2 border border-black bg-white hover:bg-black hover:text-white text-[11px] uppercase tracking-widest font-mono">
                      <Send className="w-4 h-4" /> Envoyer un push de test
                    </button>
                    <button onClick={deactivate} disabled={busy} data-testid="push-deactivate"
                      className="min-h-[44px] px-4 flex items-center gap-2 border border-[var(--accent-strong)] text-[var(--accent-strong)] hover:bg-[var(--accent-strong)] hover:text-white text-[11px] uppercase tracking-widest font-mono">
                      <BellOff className="w-4 h-4" /> Désactiver
                    </button>
                  </>
                )}
              </div>

              {sub && cats.length > 0 && (
                <div className="pt-2 border-t border-[var(--border-default)]" data-testid="push-cats">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">
                    Catégories qui déclenchent un push
                  </div>
                  <div className="space-y-1.5">
                    {cats.map((c) => (
                      <label key={c.id} className="flex items-center gap-2 text-sm min-h-[36px]"
                             data-testid={`push-cat-${c.id}`}>
                        <input type="checkbox" className="w-5 h-5 accent-black"
                          checked={isOptedIn(c.id)}
                          onChange={() => toggleCategoryOptIn(c.id)} />
                        <span className="w-3 h-3" style={{ background: c.color }} />
                        <span>{c.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs text-[var(--text-muted)] leading-relaxed">
                Les alertes urgentes déclenchent un push sonore, les autres arrivent en silencieux.
                Telegram continue d'envoyer en parallèle si tu l'as configuré au niveau de la catégorie.
              </p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
